import { Link } from 'react-router-dom';
import { MapPin, ArrowUpRight, CalendarClock, Building2, BadgeCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CompanyAvatar, Chip, DemoBadge, RecentBadge, SponsoredBadge, CategoryIcon } from '@/components/common/Primitives';
import { ContactDialog } from '@/components/common/Dialogs';
import { OPP_TYPE_BY_ID } from '@/data/taxonomy';
import { formatDate, isRecent, relativeDate, daysUntil } from '@/lib/format';
import { useData } from '@/hooks/useData';
import { cn } from '@/lib/utils';

export const CompanyCard = ({ company }) => {
  const { categoryById, myCompany } = useData();
  const cat = categoryById[company.categoryId];
  const isMine = !!myCompany && myCompany.id === company.id;
  return (
    <article className="surface surface-hover group flex h-full flex-col p-4" data-testid={`company-card-${company.id}`}>
      <div className="flex items-start gap-3">
        <CompanyAvatar company={company} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <h3 className="truncate font-sans text-sm font-semibold text-foreground">{company.name}</h3>
            {company.verified && <BadgeCheck className="h-4 w-4 shrink-0 text-primary" aria-label="Perfil verificado" />}
          </div>
          <div className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
            <CategoryIcon name={cat?.icon} className="h-3.5 w-3.5 text-primary/80" /> {cat?.name || 'Sem categoria'}
          </div>
          <div className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
            <MapPin className="h-3.5 w-3.5" /> {company.location.city} · {company.location.uf}
          </div>
        </div>
      </div>
      <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-muted-foreground">{company.tagline || company.description}</p>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {(company.services || []).slice(0, 3).map((s) => <Chip key={s}>{s}</Chip>)}
        {(company.services || []).length > 3 && <Chip className="text-muted-foreground">+{company.services.length - 3}</Chip>}
      </div>
      <div className="mt-auto flex items-center justify-between gap-2 pt-4">
        <div className="flex flex-wrap items-center gap-1.5">
          {company.isDemo ? <DemoBadge label="Fictícia" /> : isMine && <DemoBadge label="Criada por você" />}
          {company.sponsored && <SponsoredBadge />}
        </div>
        <div className="flex items-center gap-1.5">
          {!isMine && <ContactDialog company={company} trigger={<Button size="sm" variant="ghost" className="text-muted-foreground" data-testid={`company-card-contact-${company.id}`}>Contato</Button>} />}
          <Button asChild size="sm" variant="secondary" className="border border-white/10" data-testid={`company-card-view-${company.id}`}>
            <Link to={`/empresas/${company.id}`}>Ver perfil <ArrowUpRight /></Link>
          </Button>
        </div>
      </div>
    </article>
  );
};

export const OpportunityCard = ({ opp, dense = false }) => {
  const { companyById, categoryById } = useData();
  const company = companyById[opp.companyId];
  const recent = isRecent(opp.publishedAt);
  const type = OPP_TYPE_BY_ID[opp.type];
  const left = daysUntil(opp.deadline);
  return (
    <article
      className={cn('surface surface-hover relative flex flex-col gap-3 overflow-hidden p-4 sm:flex-row sm:items-start', recent && 'border-[rgba(255,59,92,0.22)]')}
      data-testid={`opportunity-card-${opp.id}`}
    >
      {recent && <span className="absolute inset-y-0 left-0 w-[2px] bg-[#ff3b5c] shadow-[0_0_12px_#ff3b5c]" aria-hidden="true" />}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          {recent ? <RecentBadge /> : <span className="rounded-md border border-white/10 px-1.5 py-0.5 text-[11px] text-muted-foreground">Publicada {relativeDate(opp.publishedAt)}</span>}
          <Chip className="border-cyan-300/20 bg-cyan-300/5 text-cyan-100">{type?.short}</Chip>
          <Chip>{categoryById[opp.categoryId]?.name}</Chip>
          {opp.sponsored && <SponsoredBadge />}
          {opp.status === 'encerrada' && <Chip className="text-muted-foreground">Encerrada</Chip>}
        </div>
        <h3 className="mt-2 font-sans text-[15px] font-semibold leading-snug text-foreground">
          <Link to={`/oportunidades/${opp.id}`} className="hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{opp.title}</Link>
        </h3>
        {!dense && <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{opp.summary || opp.description}</p>}
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><Building2 className="h-3.5 w-3.5" /> {company?.name}</span>
          <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {opp.location.city} · {opp.location.uf}</span>
          <span className="font-mono">{formatDate(opp.publishedAt)}</span>
          {opp.deadline && (
            <span className={cn('flex items-center gap-1 font-mono', left != null && left <= 7 && left >= 0 && 'text-amber-200')}>
              <CalendarClock className="h-3.5 w-3.5" /> {left < 0 ? 'Prazo encerrado' : `Prazo: ${formatDate(opp.deadline)}`}
            </span>
          )}
          {opp.isDemo && <DemoBadge label="Exemplo" />}
        </div>
      </div>
      <div className="flex shrink-0 gap-2 sm:flex-col">
        <Button asChild size="sm" className="btn-glow" data-testid={`opportunity-card-view-${opp.id}`}>
          <Link to={`/oportunidades/${opp.id}`}>Ver oportunidade</Link>
        </Button>
        {company && (
          <ContactDialog company={company} opportunity={opp} trigger={<Button size="sm" variant="secondary" className="border border-white/10" data-testid={`opportunity-card-contact-${opp.id}`}>Entrar em contato</Button>} />
        )}
      </div>
    </article>
  );
};